import React from 'react';
import { useSelector } from 'react-redux';
import { PasswordForm, RegistrationSuccess, UsernameForm } from 'shared-portal-component';

import type { RootState } from '@/store/rootReducer';
import {
  registrationNextStep,
  registrationSetData,
  registrationSetStep,
} from '@/store/slices/auth/authSlice';
import type { RegistrationData } from '@/types/authState.types';
import EmailConfirmation from '@/components/organism/Registration/EmailConfirmation';
import PhoneConfirmation from '@/components/organism/Registration/PhoneConfirmation';
import { setPasswordThunk } from '@/store/thunks/auth/setPasswordThunk';
import { useAppDispatch } from '@/store';
import { verifyOtpThunk } from '@/store/thunks/auth/verifyOtpThunk';
import { shouldBypassPhoneVerification } from '@/utils/phoneVerificationBypass';

// Array of step components
const stepComponentMap = {
  otp: UsernameForm,
  emailCode: EmailConfirmation,
  phoneCode: PhoneConfirmation,
  password: PasswordForm,
  complete: RegistrationSuccess,
} as const;

const Register: React.FC = () => {
  const dispatch = useAppDispatch();
  const { step, data, loading, error } = useSelector((state: RootState) => state.auth.registration);

  // Dynamically get the current step component
  // eslint-disable-next-line @typescript-eslint/prefer-destructuring -- already destructured
  const CurrentStep = stepComponentMap[step];

  // Handler for step navigation
  const handleNext = async(formValues: RegistrationData) => {
    dispatch(registrationSetData(formValues));
    if (step === 'otp') {
      if (!formValues.username || !formValues.otp) return;

      const result = await dispatch(
        verifyOtpThunk({
          username: formValues.username,
          otp: formValues.otp,
        })
      );
      if (verifyOtpThunk.rejected.match(result)) {
        return;
      }
    }
    if (step === 'password') {
      const {username} = data;

      if (!username || !formValues.password) {
        return;
      }

      const resultAction = await dispatch(
        setPasswordThunk({
          username,
          password: formValues.password,
        })
      );

      if (setPasswordThunk.rejected.match(resultAction)) {
        return;
      }
    }

    if (step === 'emailCode' && shouldBypassPhoneVerification()) {
      dispatch(registrationSetStep('password'));
      return;
    }

    dispatch(registrationNextStep());
  };

  return <CurrentStep formData={data} onNext={handleNext} loading={loading} error={error} />;
};

export default Register;
